import { useState, useEffect } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { LogOut, User, Edit, Save, CreditCard, Check, AlertCircle } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { UserService } from "../services/supabase";

interface HeaderProps {
  user?: any;
  onSignOut?: () => void;
}

export const Header = ({ user, onSignOut }: HeaderProps) => {
  const [showProfileDialog, setShowProfileDialog] = useState(false);
  const [currentUser, setCurrentUser] = useState<any>(user);
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [isUpdatingProfile, setIsUpdatingProfile] = useState(false);
  const [profileForm, setProfileForm] = useState({
    email: "",
    newEmail: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [userService] = useState(() => new UserService());
  const [profileMessage, setProfileMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);
  
  // Load current user when dialog opens
  useEffect(() => {
    if (showProfileDialog) {
      const loadCurrentUser = async () => {
        try {
          const { user: userData, error } = await userService.getCurrentUser();
          if (error) {
            console.error("Error loading current user:", error);
            return;
          }
          if (userData) {
            setCurrentUser(userData);
            setProfileForm(prev => ({
              ...prev,
              email: userData.email || "",
            }));
          }
        } catch (error) {
          console.error("Error loading current user:", error);
        }
      };
      loadCurrentUser();
    }
  }, [showProfileDialog, userService]);
  
  const handleStartEditingProfile = () => {
    setIsEditingProfile(true);
    setProfileForm(prev => ({
      ...prev,
      newEmail: prev.email,
      newPassword: "",
      confirmPassword: "",
    }));
  };
  
  const handleCancelEditingProfile = () => {
    setIsEditingProfile(false);
    setProfileForm(prev => ({
      ...prev,
      newEmail: "",
      newPassword: "",
      confirmPassword: "",
    }));
  };
  
  const handleUpdateProfile = async () => {
    if (!currentUser) {
      setProfileMessage({ type: "error", text: "No user logged in" });
      return;
    }
    
    setIsUpdatingProfile(true);
    setProfileMessage(null);
    try {
      const updates: { email?: string; password?: string } = {};
      
      // Check if email is being changed
      if (profileForm.newEmail !== profileForm.email) {
        if (!profileForm.newEmail) {
          setProfileMessage({ type: "error", text: "Email cannot be empty" });
          setIsUpdatingProfile(false);
          return;
        }
        updates.email = profileForm.newEmail;
      }
      
      // Check if password is being changed
      if (profileForm.newPassword) {
        if (profileForm.newPassword !== profileForm.confirmPassword) {
          setProfileMessage({ type: "error", text: "New passwords do not match" });
          setIsUpdatingProfile(false);
          return;
        }
        if (profileForm.newPassword.length < 6) {
          setProfileMessage({ type: "error", text: "Password must be at least 6 characters long" });
          setIsUpdatingProfile(false);
          return;
        }
        updates.password = profileForm.newPassword;
      }
      
      // If no changes, show message
      if (Object.keys(updates).length === 0) {
        setProfileMessage({ type: "error", text: "No changes to save" });
        setIsUpdatingProfile(false);
        return;
      }
      
      const { error } = await userService.updateUserProfile(updates);
      
      if (error) {
        setProfileMessage({ type: "error", text: String((error as any).message || error) });
        setIsUpdatingProfile(false);
        return;
      }
      
      // Update local state
      if (updates.email) {
        setProfileForm(prev => ({
          ...prev,
          email: updates.email!
        }));
      }
      
      setIsEditingProfile(false);
      setProfileForm(prev => ({
        ...prev,
        newEmail: "",
        newPassword: "",
        confirmPassword: "",
      }));
      
      setProfileMessage({ type: "success", text: "Profile updated successfully!" });
      // Reload user data
      const { user: userData } = await userService.getCurrentUser();
      if (userData) {
        setCurrentUser(userData);
      }
    } catch (error: any) {
      console.error("Error updating profile:", error);
      setProfileMessage({ type: "error", text: error?.message || "Failed to update profile" });
    } finally {
      setIsUpdatingProfile(false);
    }
  };
  return (
    <header className="sticky top-0 z-50 w-full border-b bg-card/95 backdrop-blur supports-[backdrop-filter]:bg-card/60 pt-2">
      <div className="container flex min-h-14 items-center justify-between pb-2">
        <div className="flex items-center gap-0 overflow-visible -ml-4">
          <img
            src="/logos/pink_dolphin_logo.png"
            alt="Pink Dolphin logo"
            className="h-12 w-12 object-contain shrink-0 -mr-2"
          />
          <div
            className="h-[52px] min-w-[160px] max-w-[240px] flex-1 overflow-visible bg-gradient-to-r from-primary to-tertiary py-1 -translate-x-1 -translate-y-0.5 -ml-2"
            style={{
              WebkitMaskImage: "url('/logos/pink_dolphin_text.png')",
              maskImage: "url('/logos/pink_dolphin_text.png')",
              WebkitMaskSize: "contain",
              maskSize: "contain",
              WebkitMaskRepeat: "no-repeat",
              maskRepeat: "no-repeat",
              WebkitMaskPosition: "center",
              maskPosition: "center",
            }}
            role="img"
            aria-label="Pink Dolphin Tracker - Professional Trading Card Analytics"
          />
        </div>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" className="gap-2">
              <User className="h-4 w-4" />
              <span className="hidden sm:inline">{user?.email || "charlie.maikis@gmail.com"}</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56 bg-popover">
            <DropdownMenuLabel>My Account</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => setShowProfileDialog(true)}>
              <User className="mr-2 h-4 w-4" />
              Profile
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => {
                window.location.href = '/subscription';
              }}
            >
              <CreditCard className="mr-2 h-4 w-4" />
              Subscription & Billing
            </DropdownMenuItem>
            <DropdownMenuItem onClick={onSignOut}>
              <LogOut className="mr-2 h-4 w-4" />
              Sign Out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        
        {/* Profile Dialog */}
        <Dialog open={showProfileDialog} onOpenChange={(open) => {
          setShowProfileDialog(open);
          if (!open) setProfileMessage(null);
        }}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Profile Settings</DialogTitle>
              <DialogDescription>
                Manage your account information
              </DialogDescription>
            </DialogHeader>
            {profileMessage && (
              <div className={`flex items-center gap-3 p-3 rounded-lg border text-sm ${
                profileMessage.type === "success"
                  ? "bg-success/10 border-success/20 text-success"
                  : "bg-destructive/10 border-destructive/20 text-destructive"
              }`}>
                {profileMessage.type === "success" ? (
                  <Check className="h-4 w-4 flex-shrink-0" />
                ) : (
                  <AlertCircle className="h-4 w-4 flex-shrink-0" />
                )}
                <span>{profileMessage.text}</span>
              </div>
            )}
            {currentUser ? (
              <div className="space-y-6 py-4">
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="profile-current-email">Current Email</Label>
                    <Input
                      id="profile-current-email"
                      value={profileForm.email}
                      disabled
                      className="bg-muted"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="profile-account-created">Account Created</Label>
                    <Input
                      id="profile-account-created"
                      value={currentUser.created_at ? new Date(currentUser.created_at).toLocaleDateString() : "Unknown"}
                      disabled
                      className="bg-muted"
                    />
                  </div>
                </div>
                
                {!isEditingProfile ? (
                  <Button variant="outline" onClick={handleStartEditingProfile} className="gap-2 w-full">
                    <Edit className="h-4 w-4" />
                    Edit Profile
                  </Button>
                ) : (
                  <div className="space-y-4 pt-4 border-t">
                    <div className="font-semibold">Update Profile Information</div>
                    
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="profile-new-email">New Email Address</Label>
                        <Input
                          id="profile-new-email"
                          type="email"
                          value={profileForm.newEmail}
                          onChange={(e) => setProfileForm(prev => ({ ...prev, newEmail: e.target.value }))}
                          placeholder="Enter new email (optional)"
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="profile-new-password">New Password</Label>
                        <Input
                          id="profile-new-password"
                          type="password"
                          value={profileForm.newPassword}
                          onChange={(e) => setProfileForm(prev => ({ ...prev, newPassword: e.target.value }))}
                          placeholder="Enter new password (optional)"
                        />
                      </div>
                      
                      {profileForm.newPassword && (
                        <div className="space-y-2">
                          <Label htmlFor="profile-confirm-password">Confirm New Password</Label>
                          <Input
                            id="profile-confirm-password"
                            type="password"
                            value={profileForm.confirmPassword}
                            onChange={(e) => setProfileForm(prev => ({ ...prev, confirmPassword: e.target.value }))}
                            className={profileForm.newPassword !== profileForm.confirmPassword ? "border-destructive" : ""}
                          />
                          {profileForm.newPassword !== profileForm.confirmPassword && (
                            <p className="text-sm text-destructive">Passwords do not match</p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-4">
                <p className="text-muted-foreground">Loading profile information...</p>
              </div>
            )}
            
            <DialogFooter>
              {isEditingProfile ? (
                <>
                  <Button
                    variant="outline"
                    onClick={handleCancelEditingProfile}
                    disabled={isUpdatingProfile}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="gradient"
                    onClick={handleUpdateProfile}
                    disabled={isUpdatingProfile}
                    className="gap-2"
                  >
                    <Save className="h-4 w-4" />
                    {isUpdatingProfile ? "Updating..." : "Save Changes"}
                  </Button>
                </>
              ) : (
                <Button variant="outline" onClick={() => setShowProfileDialog(false)}>
                  Close
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </header>
  );
};
